import type { Selection } from './SidePanel';
import type { WallTarget, WallTargetDetail } from './selectionBridge';
import {
  hotspotToQueueItem,
  smokeToQueueItem,
  iceStormToQueueItem,
  floodToQueueItem,
  capToQueueItem,
  type QueueItem,
} from './anomalyQueue';

/**
 * COP → WALL escalate mapping (#222).
 *
 * Maps a detail-rail `Selection` to the compact `WallTarget` the operator can
 * "Send to wall". Pure (no React / fetch / Date.now) so the payload shape + which
 * selections are escalatable are unit-testable.
 *
 * Escalatable kinds: a CAMERA (frame that exact feed) and the INCIDENT-LIKE hazard
 * selections (high-risk hotspot, camera-smoke anomaly, ice-storm, flood, CAP alert)
 * — the things a room would actually promote to the shared wall. Reuses the queue
 * converters so the wall profile reads identically to the Urgent-Alerts queue.
 * Non-incident furniture (assets, sensors, sectors, boundaries…) returns null → no
 * "Send to wall" control on those panels.
 */

const RL_LABEL = (rl: number | null): string => (rl == null ? '—' : `Level ${rl}`);
const STATE_LABEL: Record<string, string> = {
  new: 'New',
  working: 'Working',
  resolving: 'Resolving',
};

/** Build an incident `WallTarget` from a queue item (the shared hazard model). */
function fromQueueItem(item: QueueItem): WallTarget {
  const details: WallTargetDetail[] = [
    { label: 'Hazard', value: `${item.hazardIcon} ${item.hazard.replace('_', ' ')}` },
    { label: 'Response level', value: RL_LABEL(item.responseLevel) },
    { label: 'Owner', value: item.owner ?? 'unassigned' },
    { label: 'State', value: item.acknowledged ? 'Acknowledged' : STATE_LABEL[item.microStatus] ?? 'New' },
    { label: 'Source', value: item.kicker },
  ];
  return {
    kind: 'incident',
    id: item.id,
    label: item.title,
    lat: item.lat,
    lon: item.lon,
    sublabel: `${item.hazardIcon} ${RL_LABEL(item.responseLevel)}`,
    details,
  };
}

export function selectionToWallTarget(selected: Selection): WallTarget | null {
  if (!selected) return null;
  switch (selected.kind) {
    case 'camera': {
      const f = selected.feature;
      const [lon, lat] = f.geometry.coordinates;
      return {
        kind: 'camera',
        id: f.id,
        label: f.properties.name,
        lat,
        lon,
        sublabel: `${f.properties.source} · ${f.properties.status}`,
      };
    }
    case 'fire': {
      const p = selected.feature.properties;
      const [lon, lat] = p.centroid;
      const name = p.fire_id ? (p.agency ? `${p.agency} ${p.fire_id}` : p.fire_id) : p.agency ? `${p.agency} wildfire` : 'Wildland fire';
      const details: WallTargetDetail[] = [
        { label: 'Stage', value: p.stage_of_control_status ?? '—' },
        { label: 'Response', value: p.response_type ?? '—' },
      ];
      if (p.hectares_estimated != null) details.push({ label: 'Size', value: `${p.hectares_estimated} ha` });
      return {
        kind: 'incident',
        id: p.id,
        label: name,
        lat,
        lon,
        sublabel: `🔥 ${p.stage_of_control_status ?? 'fire'}`,
        details,
      };
    }
    case 'highRiskHotspot':
      return fromQueueItem(hotspotToQueueItem(selected.feature));
    case 'smokeDetectionAnomaly':
      return fromQueueItem(smokeToQueueItem(selected.feature));
    case 'iceStormAnomaly':
      return fromQueueItem(iceStormToQueueItem(selected.feature));
    case 'floodAnomaly':
      return fromQueueItem(floodToQueueItem(selected.feature));
    case 'capAlert':
      return fromQueueItem(capToQueueItem(selected.feature));
    default:
      return null;
  }
}
